
import openAIClient from "@/lib/ai/openAIClient";
import { logger } from "@/lib/logger";
import { WebsiteAnalysisResult, AnalysisErrorType } from "./types";
import { createAnalysisPrompt } from "./promptGenerator";
import { parseAIResponse } from "./responseParser";
import * as Sentry from "@sentry/react";
import { handleAIAnalysisError } from "./utils/errorHandlers";

/**
 * Analyze website content using AI
 * @param websiteContent The website content to analyze
 * @param url The original URL (for fallback and logging)
 * @returns Analysis results
 */
export async function analyzeContentWithAI(websiteContent: string, url: string): Promise<WebsiteAnalysisResult> {
  const transaction = Sentry.startTransaction({
    name: "website-analysis-ai",
    op: "ai.generate"
  });

  logger.info({
    message: "Analyzing website content with AI",
    context: { 
      urlLength: url.length, 
      contentLength: websiteContent.length,
      method: "client_side_ai"
    }
  });
  
  // If OpenAI client is not initialized, fall back to edge function
  if (!openAIClient.isInitialized) {
    logger.warn({
      message: "OpenAI client not initialized",
      context: { url }
    });
    
    Sentry.addBreadcrumb({
      category: 'ai',
      message: 'OpenAI client not initialized',
      level: 'warning'
    });
    
    transaction.finish();
    throw new Error("OpenAI client not initialized");
  }
  
  try {
    Sentry.configureScope(scope => {
      scope.setTag("analysis_method", "client_side_ai");
      scope.setContext("ai_parameters", { 
        contentSize: websiteContent.length,
        url 
      });
    });

    // Create analysis prompt
    const analysisPrompt = createAnalysisPrompt();
    
    // Generate AI response
    const aiResponse = await openAIClient.generate({
      systemPrompt: analysisPrompt,
      userPrompt: websiteContent.substring(0, 8000), // Limit content to avoid token limits
      config: {
        model: "gpt-4o-mini", // Use mini model to save costs
        temperature: 0.2,
        maxTokens: 1000
      }
    });
    
    Sentry.addBreadcrumb({
      category: 'ai',
      message: 'AI response generated successfully',
      level: 'info',
      data: {
        responseLength: aiResponse.length
      }
    });

    // Parse the response
    const result = parseAIResponse(aiResponse);
    transaction.finish();
    return result;
  } catch (error) {
    handleAIAnalysisError(error, url);
    transaction.finish();
    throw error;
  }
}
